import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { QRCodeCanvas } from '@cheprasov/qrcode';

// QR Code component using @cheprasov/qrcode library
const QRCodeComponent = ({ value, size }: { value: string; size: number }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Generate QR code using @cheprasov/qrcode
      const qrCode = new QRCodeCanvas(value, {
        size,
        level: 'H', // High error correction for better readability
      });

      setQrDataUrl(qrCode.toDataURL());
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [value, size]);

  if (error) {
    return (
      <Box
        border="1px solid"
        borderColor="red.300"
        p={4}
        borderRadius="md"
        width="100%"
        height={`${size}px`}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Text color="red.500" fontSize="md" textAlign="center">
          Error generating QR code:
        </Text>
        <Text fontSize="sm" mt={2} textAlign="center">
          {error}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor="grey.300"
      p={4}
      borderRadius="md"
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      {qrDataUrl ? (
        <img src={qrDataUrl} alt="QR Code" width={size} height={size} />
      ) : (
        <Text fontSize="md" textAlign="center">
          Generating QR Code...
        </Text>
      )}
      <Text fontSize="xs" mt={2} color="grey.500" textAlign="center">
        {value}
      </Text>
    </Box>
  );
};

export type SettingsStep = {
  type: 'qr' | 'text';
  title?: string;
  content: string;
};

export type ScannerSettingsQRCodeProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  steps: SettingsStep[]; // Required prop for settings
};

export default function ScannerSettingsQRCode({ isOpen, onClose, title, steps }: ScannerSettingsQRCodeProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box display="flex" flexDirection="column" alignItems="center" p={4}>
            <Text fontSize="md" fontWeight="bold" mb={4} textAlign="center">
              Scanner HW0009 Settings
            </Text>

            {steps.map((step, index) => (
              <Box key={index} mb={index < steps.length - 1 ? 6 : 0} width="100%">
                {step.title && (
                  <Heading size="sm" mb={2} textAlign="center">
                    {step.title}
                  </Heading>
                )}

                {step.type === 'qr' ? (
                  <QRCodeComponent value={step.content} size={256} />
                ) : (
                  <Box border="1px solid" borderColor="grey.300" p={4} borderRadius="md" width="100%">
                    <Text fontSize="md" textAlign="center">
                      {step.content}
                    </Text>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
